import { InputField } from "../../common/ui/inputField";
import { Button } from "../../common/ui/button/button";
import { DeleteButton } from "../../common/ui/button/icon/deleteButton";
import { ReportSectionCard } from "./reportSectionCard";
import { SectionRecipientsList } from "./sectionRecipientsList";
import { PaymentFailedNoticeCard } from "./paymentFailedNoticeCard";
import { usePaymentMethods } from "../../../hooks/paymentMethod/usePaymentMethods";
import { useModuleEnabled } from "../../../hooks/gym/useModuleEnabled";
import { useCurrentGym } from "../../../hooks/gym/useCurrentGym";
import {
  MAX_INACTIVITY_DAYS,
  MIN_INACTIVITY_DAYS,
} from "../../../utils/zod/admin";
import type {
  AdminSectionState,
  BasicSectionState,
  EmailSectionKey,
  InactivitySectionState,
  LowStockAlertsSectionState,
  RemindersSectionState,
} from "../../../hooks/admin/gym/useGymEmailConfigForm";

const lowStockDescriptionFor = (
  inventoryEnabled: boolean,
  productCatalogEnabled: boolean,
): string => {
  if (inventoryEnabled && !productCatalogEnabled) {
    return "Recibe un correo cuando un producto del inventario cruce su stock mínimo. Las alertas se disparan en pagos de food y merchandise.";
  }
  if (productCatalogEnabled && !inventoryEnabled) {
    return "Recibe un correo cuando un material cruce su stock mínimo. Las alertas se disparan al aprobar órdenes de venta.";
  }
  return "Recibe un correo cuando un material o producto del inventario cruce su stock mínimo. Las alertas se disparan al aprobar órdenes de venta y al registrar pagos de food o merchandise.";
};

interface GymEmailConfigFieldsProps {
  adminReport: AdminSectionState;
  setAdminReport: (patch: Partial<AdminSectionState>) => void;
  salesReport: BasicSectionState;
  setSalesReport: (patch: Partial<BasicSectionState>) => void;
  reminders: RemindersSectionState;
  setReminders: (patch: Partial<RemindersSectionState>) => void;
  lowStockAlerts: LowStockAlertsSectionState;
  setLowStockAlerts: (patch: Partial<LowStockAlertsSectionState>) => void;
  paymentFailedEnabled: boolean;
  setPaymentFailedEnabled: (enabled: boolean) => void;
  inactivity: InactivitySectionState;
  setInactivity: (patch: Partial<InactivitySectionState>) => void;
  addRecipient: (section: EmailSectionKey) => void;
  removeRecipient: (section: EmailSectionKey, email: string) => void;
  addExcludeKey: (key: string) => void;
  removeExcludeKey: (key: string) => void;
}

//TODO: Remove this logic in front and back (exclusión de recordatorios por método de pago)
const SHOW_EXCLUDE_PAYMENT_METHODS = false;

const ExcludePaymentMethods = ({
  excludeKeys,
  onAdd,
  onRemove,
}: {
  excludeKeys: string[];
  onAdd: (key: string) => void;
  onRemove: (key: string) => void;
}) => {
  const { data: paymentMethods = [] } = usePaymentMethods();
  const available = paymentMethods.filter(
    (pm) => !excludeKeys.includes(pm.key),
  );

  return (
    <div className="space-y-2 p-3 bg-global-primary rounded-md border border-global-secondary">
      <p className="text-size-2xs font-semibold text-global-secondary">
        Excluir recordatorios a usuarios con suscripciones que usen los
        siguientes métodos de pago
      </p>

      {available.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {available.map((pm) => (
            <Button
              key={pm.key}
              type="button"
              onClick={() => onAdd(pm.key)}
              className="btn-compact-sm"
            >
              + {pm.name}
            </Button>
          ))}
        </div>
      )}

      {excludeKeys.length > 0 && (
        <ul className="space-y-1 mt-2">
          {excludeKeys.map((key) => {
            const pm = paymentMethods.find((p) => p.key === key);
            return (
              <li
                key={key}
                className="flex items-center justify-between px-3 py-1.5 rounded-md bg-global-overlay-secondary border border-global-secondary"
              >
                <span className="text-size-xs text-global-primary font-medium">
                  {pm?.name ?? key}
                </span>
                <DeleteButton onClick={() => onRemove(key)} title="Eliminar" />
              </li>
            );
          })}
        </ul>
      )}

      {excludeKeys.length === 0 && (
        <p className="text-size-2xs text-global-muted">
          No se excluyen usuarios con suscripciones de métodos de pago
        </p>
      )}
    </div>
  );
};

export const GymEmailConfigFields = ({
  adminReport,
  setAdminReport,
  salesReport,
  setSalesReport,
  reminders,
  setReminders,
  lowStockAlerts,
  setLowStockAlerts,
  paymentFailedEnabled,
  setPaymentFailedEnabled,
  inactivity,
  setInactivity,
  addRecipient,
  removeRecipient,
  addExcludeKey,
  removeExcludeKey,
}: GymEmailConfigFieldsProps) => {
  const { data: gym } = useCurrentGym();
  const inventoryEnabled = useModuleEnabled("inventory");
  const productCatalogEnabled = useModuleEnabled("productCatalog");
  const showLowStockAlerts = inventoryEnabled || productCatalogEnabled;
  const onlinePaymentsEnabled = useModuleEnabled("onlinePayments");
  const lowStockDescription = lowStockDescriptionFor(
    inventoryEnabled,
    productCatalogEnabled,
  );

  return (
    <>
      <p className="text-size-2xs text-global-tertiary">
        Las horas de envío usan la zona horaria del negocio
        {gym?.timezone ? ` (${gym.timezone})` : ""}.
      </p>

      <ReportSectionCard
        title="Reporte de administración"
        enabled={adminReport.enabled}
        onToggle={() => setAdminReport({ enabled: !adminReport.enabled })}
        time={adminReport.time}
        onTimeChange={(v) => setAdminReport({ time: v })}
        timeId="adminReportTime"
        timeLabel="Hora de envío"
        subject={adminReport.subject}
        onSubjectChange={(v) => setAdminReport({ subject: v })}
        subjectId="adminReportSubject"
        subjectLabel="Asunto (opcional)"
        subjectPlaceholder="Ej: Reporte de administración"
        recipientsSlot={
          <SectionRecipientsList
            recipients={adminReport.recipients}
            newRecipient={adminReport.newRecipient}
            onNewRecipientChange={(v) => setAdminReport({ newRecipient: v })}
            onAdd={() => addRecipient("admin")}
            onRemove={(e) => removeRecipient("admin", e)}
          />
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            id="adminReportExpiringSoonDays"
            type="number"
            label="Días próximos a vencer"
            value={String(adminReport.expiringSoonDays)}
            onChange={(e) =>
              setAdminReport({ expiringSoonDays: Number(e.target.value) })
            }
          />
          <InputField
            id="adminReportRecentlyExpiredDays"
            type="number"
            label="Días vencidos recientemente"
            value={String(adminReport.recentlyExpiredDays)}
            onChange={(e) =>
              setAdminReport({ recentlyExpiredDays: Number(e.target.value) })
            }
          />
        </div>
      </ReportSectionCard>

      <ReportSectionCard
        title="Reporte de ventas"
        enabled={salesReport.enabled}
        onToggle={() => setSalesReport({ enabled: !salesReport.enabled })}
        time={salesReport.time}
        onTimeChange={(v) => setSalesReport({ time: v })}
        timeId="salesReportTime"
        timeLabel="Hora de envío"
        subject={salesReport.subject}
        onSubjectChange={(v) => setSalesReport({ subject: v })}
        subjectId="salesReportSubject"
        subjectLabel="Asunto (opcional)"
        subjectPlaceholder="Ej: Reporte de ventas"
        recipientsSlot={
          <SectionRecipientsList
            recipients={salesReport.recipients}
            newRecipient={salesReport.newRecipient}
            onNewRecipientChange={(v) => setSalesReport({ newRecipient: v })}
            onAdd={() => addRecipient("sales")}
            onRemove={(e) => removeRecipient("sales", e)}
          />
        }
      />

      <ReportSectionCard
        title="Recordatorios de suscripciones"
        enabled={reminders.enabled}
        onToggle={() => setReminders({ enabled: !reminders.enabled })}
        time={reminders.time}
        onTimeChange={(v) => setReminders({ time: v })}
        timeId="subscriptionRemindersTime"
        timeLabel="Hora de envío"
        subject={reminders.subject}
        onSubjectChange={(v) => setReminders({ subject: v })}
        subjectId="subscriptionRemindersSubject"
        subjectLabel="Asunto (opcional)"
        subjectPlaceholder="Ej: Tu suscripción vence pronto"
        recipientsSlot={
          <SectionRecipientsList
            recipients={reminders.recipients}
            newRecipient={reminders.newRecipient}
            onNewRecipientChange={(v) => setReminders({ newRecipient: v })}
            onAdd={() => addRecipient("reminders")}
            onRemove={(e) => removeRecipient("reminders", e)}
          />
        }
      >
        {SHOW_EXCLUDE_PAYMENT_METHODS && (
          <ExcludePaymentMethods
            excludeKeys={reminders.excludePaymentMethodKeys}
            onAdd={addExcludeKey}
            onRemove={removeExcludeKey}
          />
        )}
      </ReportSectionCard>

      <ReportSectionCard
        title="Correo de inactividad"
        description="Le llega al socio con plan activo que lleva varios días sin asistir. Se envía una sola vez hasta que vuelva a venir."
        enabled={inactivity.enabled}
        onToggle={() => setInactivity({ enabled: !inactivity.enabled })}
        time={inactivity.time}
        onTimeChange={(v) => setInactivity({ time: v })}
        timeId="inactivityRemindersTime"
        timeLabel="Hora de envío"
        subject={inactivity.subject}
        onSubjectChange={(v) => setInactivity({ subject: v })}
        subjectId="inactivityRemindersSubject"
        subjectLabel="Asunto (opcional)"
        subjectPlaceholder="Ej: Te extrañamos"
      >
        <InputField
          id="inactivityRemindersDays"
          type="number"
          label="Días sin asistir"
          min={MIN_INACTIVITY_DAYS}
          max={MAX_INACTIVITY_DAYS}
          required
          value={inactivity.inactiveDays ? String(inactivity.inactiveDays) : ""}
          onChange={(e) =>
            setInactivity({ inactiveDays: Number(e.target.value) })
          }
        />
      </ReportSectionCard>

      {showLowStockAlerts && (
        <ReportSectionCard
          title="Alertas de stock bajo"
          description={lowStockDescription}
          enabled={lowStockAlerts.enabled}
          onToggle={() =>
            setLowStockAlerts({ enabled: !lowStockAlerts.enabled })
          }
          subject={lowStockAlerts.subject}
          onSubjectChange={(v) => setLowStockAlerts({ subject: v })}
          subjectId="lowStockAlertsSubject"
          subjectLabel="Asunto (opcional)"
          subjectPlaceholder="Ej: Alerta de stock bajo"
          recipientsSlot={
            <SectionRecipientsList
              recipients={lowStockAlerts.recipients}
              newRecipient={lowStockAlerts.newRecipient}
              onNewRecipientChange={(v) =>
                setLowStockAlerts({ newRecipient: v })
              }
              onAdd={() => addRecipient("lowStockAlerts")}
              onRemove={(e) => removeRecipient("lowStockAlerts", e)}
            />
          }
        />
      )}

      {onlinePaymentsEnabled && (
        <PaymentFailedNoticeCard
          enabled={paymentFailedEnabled}
          onToggle={setPaymentFailedEnabled}
        />
      )}
    </>
  );
};
